import api from './api';
import { 
  FinancialInstitutionResponse, 
  CreateFinancialInstitutionRequest,
  UpdateFinancialInstitutionRequest,
  FinancialInstitutionFilters 
} from '../types/financialInstitution';
import { ApiResponse } from '../types/api';

/**
 * Service pour la gestion des institutions financières
 * ✨ SIMPLIFIÉ: Transmet TOUTES les données sans transformation
 * Les formulaires sont la source de vérité
 */
class FinancialInstitutionApiService {
  private baseUrl = '/financial-institutions';

  /**
   * Crée une nouvelle institution financière
   * @param data - TOUTES les données du formulaire FinancialInstitutionForm
   */
  async createInstitution(data: CreateFinancialInstitutionRequest): Promise<FinancialInstitutionResponse> {
    console.log('📤 FinancialInstitutionApiService.createInstitution - Envoi de TOUTES les données:', data);
    const response = await api.post<ApiResponse<FinancialInstitutionResponse>>(this.baseUrl, data);
    console.log('✅ FinancialInstitutionApiService.createInstitution - Réponse reçue:', response.data);
    return response.data;
  }

  /**
   * Récupère une institution financière par son ID
   */
  async getInstitution(institutionId: string): Promise<FinancialInstitutionResponse> {
    console.log(`📥 FinancialInstitutionApiService.getInstitution - Récupération de l'institution ${institutionId}`);
    const response = await api.get<ApiResponse<FinancialInstitutionResponse>>(`${this.baseUrl}/${institutionId}`);
    return response.data;
  }

  /**
   * Met à jour une institution financière
   * @param data - TOUTES les données modifiées (partielles ou complètes)
   */
  async updateInstitution(institutionId: string, data: UpdateFinancialInstitutionRequest): Promise<FinancialInstitutionResponse> {
    console.log(`📤 FinancialInstitutionApiService.updateInstitution - Envoi de TOUTES les données pour ${institutionId}:`, data);
    const response = await api.patch<ApiResponse<FinancialInstitutionResponse>>(`${this.baseUrl}/${institutionId}`, data);
    console.log('✅ FinancialInstitutionApiService.updateInstitution - Réponse reçue:', response.data);
    return response.data;
  }

  async uploadLogo(institutionId: string, logoFile: File): Promise<{ url: string }> {
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/logo`,
      logoFile,
      'logo'
    );
    return response.data;
  }

  async uploadCeoPhoto(institutionId: string, photoFile: File): Promise<{ url: string }> {
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/ceo-photo`,
      photoFile,
      'photo'
    );
    return response.data;
  }

  async addBranch(institutionId: string, branchData: {
    name: string;
    address: string;
    phone: string;
    manager?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${institutionId}/branches`,
      branchData
    );
    return response.data;
  }

  async removeBranch(institutionId: string, branchId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${institutionId}/branches/${branchId}`
    );
    return response.data;
  }

  async addTeamMember(institutionId: string, memberData: {
    name: string;
    position: string;
    bio?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${institutionId}/team`,
      memberData
    );
    return response.data;
  }

  async removeTeamMember(institutionId: string, memberId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${institutionId}/team/${memberId}`
    );
    return response.data;
  }

  async uploadTeamMemberPhoto(institutionId: string, memberId: string, photoFile: File): Promise<{ url: string }> {
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/team/${memberId}/photo`,
      photoFile,
      'photo'
    );
    return response.data;
  }

  async listInstitutions(filters?: FinancialInstitutionFilters): Promise<{
    institutions: FinancialInstitutionResponse[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  }> {
    const response = await api.get<ApiResponse<FinancialInstitutionResponse[]>>(this.baseUrl, filters);
    return {
      institutions: response.data,
      meta: response.meta?.pagination ? { pagination: response.meta.pagination } : { pagination: { page: 1, limit: 10, total: 0, pages: 0 } }
    };
  }
}

export const financialInstitutionApiService = new FinancialInstitutionApiService();
export default financialInstitutionApiService;
